import { API_TYPES } from "@src/appV2/api";
import { get, post } from "@src/appV2/api/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import axios, { type AxiosError } from "axios";
import { z } from "zod";

interface CreateInterviewAttendanceVerificationParams {
  interviewId: string;
  interviewerName: string;
  attendanceDescription: string;
  evidenceFile: File | undefined;
}

const presignedUrlResponseSchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.interviewAttendanceVerificationUploadPresignedUrl),
    attributes: z.object({
      url: z.string(),
      fileKey: z.string(),
    }),
  }),
});

const interviewAttendanceVerificationCreateRequestSchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.interviewAttendanceVerification),
    attributes: z.object({
      interviewerName: z.string().min(1),
      attendanceDescription: z.string().min(1),
      evidenceFileKey: z.string().optional(),
    }),
    relationships: z.object({
      interview: z.object({
        data: z.object({
          type: z.literal(API_TYPES.interview),
          id: z.string(),
        }),
      }),
    }),
  }),
});

const interviewAttendanceVerificationResponseSchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.interviewAttendanceVerification),
    id: z.string(),
    attributes: z.object({
      interviewerName: z.string(),
      attendanceDescription: z.string(),
      hasEvidenceFile: z.boolean(),
      createdAt: z.string(),
      updatedAt: z.string(),
    }),
    relationships: z.object({
      worker: z.object({
        data: z.object({
          type: z.literal(API_TYPES.worker),
          id: z.string(),
        }),
      }),
      interview: z.object({
        data: z.object({
          type: z.literal(API_TYPES.interview),
          id: z.string(),
        }),
      }),
    }),
  }),
});

async function fetchPresignedUrl(interviewId: string) {
  const response = await get({
    url: `${environmentConfig.REACT_APP_BASE_API_URL}/interview-attendance-verification-presigned-urls`,
    queryParams: { interviewId },
    responseSchema: presignedUrlResponseSchema,
  });

  return response.data.data.attributes;
}

export function useCreateInterviewAttendanceVerification(
  options: UseMutationOptions<unknown, AxiosError, CreateInterviewAttendanceVerificationParams> = {}
) {
  return useMutation({
    mutationFn: async (params: CreateInterviewAttendanceVerificationParams) => {
      const { interviewId, interviewerName, attendanceDescription, evidenceFile } = params;

      let evidenceFileKey: string | undefined;
      if (evidenceFile) {
        const { url, fileKey } = await fetchPresignedUrl(interviewId);
        await axios.put(url, evidenceFile, {
          headers: {
            "Content-Type": evidenceFile.type,
          },
        });
        evidenceFileKey = fileKey;
      }

      const response = await post({
        url: `${environmentConfig.REACT_APP_BASE_API_URL}/interview-attendance-verifications`,
        data: {
          data: {
            type: API_TYPES.interviewAttendanceVerification,
            attributes: {
              interviewerName,
              attendanceDescription,
              evidenceFileKey,
            },
            relationships: {
              interview: {
                data: {
                  type: API_TYPES.interview,
                  id: interviewId,
                },
              },
            },
          },
        },
        requestSchema: interviewAttendanceVerificationCreateRequestSchema,
        responseSchema: interviewAttendanceVerificationResponseSchema,
      });

      return response.data;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.INTERVIEW_ATTENDANCE_VERIFICATION_SUBMITTED_FAILURE,
    },
    ...options,
  });
}
